import { Controller } from "@hotwired/stimulus";
import tippy from "tippy.js";
import deepEqual from "deep-equal";

import URL from "hb/lib/url";
import { useQuery } from "hb/insights/use_query";
import { useListen } from "hb/insights/use_listen";
import { fetchEmpty, withRedirectTo, fetchJSON } from "hb/lib/fetch";
import { arraysContainSame } from "hb/lib/utils";

export default class extends Controller {
  static targets = [
    "tab",
    "selector",
    "vis",
    "options",
    "dashboardSave",
    "dashboardLink",
    "dashboardUpdateText",
  ];
  static values = {
    querySessionId: String,
    dashboardsPath: String,
    viewPath: String,
    exportPath: String,
    newAlarmPath: String,
    widgetQuery: String,
    widgetView: String,
    widgetStreamIds: Array,
    widgetChartConfig: Object,
    useParamState: { Boolean, default: true },
    widgetId: String,
  };

  connect() {
    useListen(this);

    this.view = this.hasWidgetViewValue
      ? this.widgetViewValue
      : URL.params().get("view") || "table";
    this.streamIds = JSON.parse(URL.params().get("stream_ids"));

    this.hasQueryError = false;
    useQuery(this, { sessionId: this.querySessionIdValue });
    this.listenWindow("updateVisState", this.onUpdateVisState);
  }

  addErrorMessage(message) {
    if (this.hasQueryError) return;

    const msg = document.createElement("h6");
    msg.innerHTML = message;
    msg.classList.add(
      "query-error-el",
      "dropdown-header",
      "text-red",
      "font-weight-bold",
      "text-uppercase",
    );
    const rule = document.createElement("hr");
    rule.classList.add("query-error-el", "dropdown-divider");

    if (this.hasOptionsTarget) {
      this.optionsTarget
        .querySelectorAll(".dropdown-item")
        .forEach((e) => e.classList.add("disabled"));
      this.optionsTarget.prepend(rule);
      this.optionsTarget.prepend(msg);
    }
  }

  onQueryEmpty() {
    this.addErrorMessage("Invalid query");
    this.hasQueryError = true;
    this.checkDashboardSave();
  }

  onQueryError() {
    this.addErrorMessage("Invalid query");
    this.hasQueryError = true;
    this.checkDashboardSave();
  }

  onQueryReady({ detail }) {
    const {
      query: {
        data: {
          response: { query },
        },
      },
    } = detail;

    if (this.hasQueryError && this.hasOptionsTarget) {
      this.element.classList.remove("query-error");
      this.optionsTarget
        .querySelectorAll(".dropdown-item")
        .forEach((e) => e.classList.remove("disabled"));
      this.optionsTarget
        .querySelectorAll(".query-error-el")
        .forEach((e) => e.remove());
    } else {
      this.query = query;
    }

    // I need to wait a few moments for the query to be ready
    setTimeout(() => {
      this.checkDashboardSave();
    }, 10);
    this.hasQueryError = false;
  }

  onUpdateVisState({ view }) {
    if (view && view !== this.view) {
      this.view = view;
      this.setTab();
    }
  }

  setTab() {
    this.tabTargets.forEach((target) => {
      if (target.id === this.view) {
        target.classList.add("selected");

        if (target.closest(".menu-dropdown")) {
          const btn = this.selectorTarget;

          btn.innerHTML = target.innerHTML;
          btn.id = target.id;
        }
      } else {
        target.classList.remove("selected");
      }
    });
  }

  export({ params: { format } }) {
    const link = document.createElement("a");
    link.href = `${this.exportPathValue}.${format}?${URL.params().toString()}`;
    link.click();
  }

  newAlarm() {
    const link = document.createElement("a");
    link.href = `${this.newAlarmPathValue}?${URL.slice(["query", "ts", "stream_ids"]).toString()}`;
    link.click();
  }

  toggleVis(evt) {
    const { currentTarget } = evt;
    evt.stopImmediatePropagation();

    if (this.view === currentTarget.id) {
      return;
    }

    // If inside dropdown menu
    if (currentTarget.closest(".menu-dropdown")) {
      const btn = this.selectorTarget;

      btn.innerHTML = currentTarget.innerHTML;
      btn.id = currentTarget.id;
    }

    this.emitWindow("vis:viewChange", {
      querySessionId: this.querySessionIdValue,
      view: currentTarget.id,
    });

    this.view = currentTarget.id;
    this.setTab();
  }

  get visController() {
    return this.application.getControllerForElementAndIdentifier(
      this.visTarget,
      this.visTarget.dataset.controller,
    );
  }

  get dashboardParams() {
    return {
      query: URL.getParam("query"),
      ts: URL.getParam("ts"),
      stream_ids: JSON.parse(URL.getParam("stream_ids")),
      vis: {
        view: this.view,
        chart_config: this.visController.config,
      },
    };
  }

  emitConfigUpdate() {
    if (!this.useParamStateValue && this.hasQuerySessionIdValue) {
      this.emitWindow("queryConfig:update", {
        querySessionId: this.querySessionIdValue,
        widgetId: this.widgetIdValue,
        vis: {
          view: this.view,
          chart_config: this.visController?.config,
        },
      });
    }
  }

  async addToNewDashboard() {
    if (this.hasQueryError) return;

    await withRedirectTo({ keepParams: ["ts"] })(
      fetchEmpty({
        path: this.dashboardsPathValue,
        method: "POST",
        snakeCaseBody: false,
        jsonBody: this.dashboardParams,
      }),
    );
  }

  async addToDashboard({ params: { dashboardId } }) {
    if (this.hasQueryError) return;

    await withRedirectTo({ keepParams: ["ts"] })(
      fetchEmpty({
        path: `${this.dashboardsPathValue}/${dashboardId}/widgets`,
        method: "POST",
        snakeCaseBody: false,
        jsonBody: this.dashboardParams,
      }),
    );
  }

  async updateDashboardWidget() {
    if (this.hasQueryError) return;

    await withRedirectTo({ keepParams: ["ts"] })(
      fetchEmpty({
        path: `${this.dashboardsPathValue}/${URL.getParam("dashboard")}/widgets/${URL.getParam("widget")}`,
        method: "PUT",
        snakeCaseBody: false,
        jsonBody: this.dashboardParams,
      }),
    );
  }

  checkDashboardSave() {
    if (!this.hasDashboardSaveTarget) return;

    if (
      (!this.hasQueryError &&
        (this.query !== this.widgetQueryValue ||
          this.view !== this.widgetViewValue)) ||
      !(
        this.hasWidgetStreamIdsValue &&
        arraysContainSame(this.streamIds, this.widgetStreamIdsValue)
      ) ||
      !deepEqual(this.widgetChartConfigValue, this.visController?.config)
    ) {
      this.dashboardSaveTarget.removeAttribute("disabled");
    } else {
      this.dashboardSaveTarget.setAttribute("disabled", true);
    }
  }

  async createDashboardWidget() {
    if (this.hasQueryError) return;

    await fetchEmpty({
      path: `${this.dashboardsPathValue}/${URL.getParam("dashboard")}/widgets`,
      method: "PUT",
      snakeCaseBody: false,
      jsonBody: this.dashboardParams,
    });
  }

  async saveDashboardWidget({ params: { followRedirect } }) {
    if (this.hasQueryError) return;

    const widgetId = URL.getParam("widget");
    let tippyContent = "Saved!";
    let response;
    let requestData = {
      snakeCaseBody: false,
      jsonBody: this.dashboardParams,
    };

    if (widgetId !== null) {
      requestData = {
        ...requestData,
        path: `${this.dashboardsPathValue}/${URL.getParam("dashboard")}/widgets/${widgetId}`,
        method: "PUT",
      };
    } else {
      requestData = {
        ...requestData,
        path: `${this.dashboardsPathValue}/${URL.getParam("dashboard")}/widgets`,
        method: "POST",
      };
    }

    if (followRedirect) {
      await withRedirectTo({ keepParams: ["ts"] })(fetchEmpty(requestData));
      return;
    } else {
      response = await fetchJSON(requestData);
    }

    if (widgetId === null) {
      URL.replaceParam("widget", response.id);
      this.dashboardSaveTarget.innerHTML = "Save";

      tippyContent = "Added!";
    }

    const t = tippy(this.dashboardSaveTarget, {
      content: tippyContent,
      animation: "shift-away-subtle",
      arrow: false,
      hideOnClick: false,
      trigger: "manual",
      onHidden(instance) {
        instance.destroy();
      },
    });
    t.show();
    setTimeout(() => t.hide(), 1500);

    if (widgetId === null) {
      this.dashboardUpdateTextTarget.innerHTML = "Update";
    }

    this.widgetQueryValue = this.query;
    this.widgetViewValue = this.view;

    this.checkDashboardSave();
  }
}
